import { Progress } from '@/components/ui/progress';
import { Loader2, CheckCircle, AlertCircle, Search, Sparkles, Database } from 'lucide-react';
import { ImportProgress as ImportProgressType } from '@/hooks/useImportProgress';

interface ImportProgressProps {
  progress: ImportProgressType;
  isImporting: boolean;
}

const statusConfig: Record<string, { icon: React.ElementType; label: string; color: string }> = {
  starting: { icon: Loader2, label: 'Initialisation...', color: 'text-blue-600' },
  scraping: { icon: Search, label: 'Scraping en cours', color: 'text-blue-600' },
  enriching: { icon: Sparkles, label: 'Enrichissement', color: 'text-purple-600' },
  inserting: { icon: Database, label: 'Insertion en base', color: 'text-orange-600' },
  done: { icon: CheckCircle, label: 'Terminé', color: 'text-green-600' },
  error: { icon: AlertCircle, label: 'Erreur', color: 'text-red-600' },
};

export const ImportProgressIndicator = ({ progress, isImporting }: ImportProgressProps) => {
  const config = statusConfig[progress.status] || statusConfig.starting;
  const Icon = config.icon;
  const isAnimated = progress.status !== 'done' && progress.status !== 'error';

  // Calculate overall percentage
  let percentage = 0;
  if (progress.status === 'scraping') {
    percentage = progress.total_pages > 0
      ? Math.round((progress.current_page / progress.total_pages) * 40)
      : 10;
  } else if (progress.status === 'enriching') {
    percentage = 40 + (progress.vehicles_found > 0
      ? Math.round((progress.vehicles_enriched / progress.vehicles_found) * 40)
      : 0);
  } else if (progress.status === 'inserting') {
    percentage = 85;
  } else if (progress.status === 'done') {
    percentage = 100;
  } else if (progress.status === 'error') {
    percentage = 100;
  }

  if (!isImporting && progress.status === 'done') return null;

  return (
    <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
      {/* Status header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon className={`h-4 w-4 ${config.color} ${isAnimated ? 'animate-spin' : ''}`} />
          <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
        </div>
        <span className="text-xs text-muted-foreground">{percentage}%</span>
      </div>

      {/* Progress bar */}
      <Progress value={percentage} className="h-2" />

      {/* Step description */}
      <p className="text-xs text-muted-foreground">{progress.current_step}</p>

      {/* Stats row */}
      <div className="flex space-x-4 text-xs text-muted-foreground">
        {progress.status === 'scraping' && progress.total_pages > 1 && (
          <span>Page {progress.current_page}/{progress.total_pages}</span>
        )}
        {progress.vehicles_found > 0 && (
          <span>{progress.vehicles_found} véhicule(s) trouvé(s)</span>
        )}
        {progress.vehicles_enriched > 0 && (
          <span>{progress.vehicles_enriched} enrichi(s)</span>
        )}
        {progress.vehicles_imported > 0 && (
          <span>{progress.vehicles_imported} importé(s)</span>
        )}
      </div>
    </div>
  );
};
